import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface OrderSummary {
  oppsummering: string
  spor_om: string[] | null
}

type SummaryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: OrderSummary }
  | { status: 'error'; error: string }

function getSessionCache(orderId: number): OrderSummary | null {
  try {
    const raw = sessionStorage.getItem(`order-summary-${orderId}`)
    if (!raw) return null
    return JSON.parse(raw) as OrderSummary
  } catch {
    return null
  }
}

function setSessionCache(orderId: number, data: OrderSummary): void {
  try {
    sessionStorage.setItem(`order-summary-${orderId}`, JSON.stringify(data))
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

export function useOrderSummary() {
  const [state, setState] = useState<SummaryState>({ status: 'idle' })
  const [cachedOrderId, setCachedOrderId] = useState<number | null>(null)

  const summarize = useCallback(async (orderId: number) => {
    // Return in-memory cached result if same order
    if (cachedOrderId === orderId && state.status === 'success') return

    // Check sessionStorage cache
    const cached = getSessionCache(orderId)
    if (cached) {
      setState({ status: 'success', data: cached })
      setCachedOrderId(orderId)
      return
    }

    setState({ status: 'loading' })
    setCachedOrderId(orderId)

    try {
      const { data, error } = await supabase.functions.invoke<OrderSummary>('summarize-order', {
        body: { order_id: orderId },
      })

      if (error) {
        throw new Error(error.message || 'Kunne ikke oppsummere ordren')
      }

      if (!data?.oppsummering) {
        throw new Error('Ingen oppsummering mottatt')
      }

      setSessionCache(orderId, data)
      setState({ status: 'success', data })
    } catch (err) {
      setState({
        status: 'error',
        error: err instanceof Error ? err.message : 'Ukjent feil',
      })
    }
  }, [cachedOrderId, state.status])

  const dismiss = useCallback(() => {
    setState({ status: 'idle' })
    setCachedOrderId(null)
  }, [])

  const onOrderChange = useCallback((orderId: number | null) => {
    if (orderId && getSessionCache(orderId)) {
      setState({ status: 'success', data: getSessionCache(orderId)! })
      setCachedOrderId(orderId)
    } else {
      setState({ status: 'idle' })
      setCachedOrderId(null)
    }
  }, [])

  return { state, summarize, dismiss, onOrderChange }
}
